import { Fragment, useLayoutEffect, useRef } from "react";
import nameBlockUrl from "../assets/hello/name-block.svg";
import MiniRelCoffee from "../coffee/MiniRelCoffee";
import "./HelloPage.css";

const POSITION = [
  "Complex enterprise products",
  "Human-centered AI",
  "End-to-end design",
] as const;

export default function HelloPage() {
  const nameRef = useRef<HTMLImageElement>(null);
  const roleRef = useRef<HTMLParagraphElement>(null);
  const positionRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const name = nameRef.current;
    const role = roleRef.current;
    if (!name || !role) return;

    const mobile = window.matchMedia("(max-width: 599px)");
    const fit = () => {
      if (mobile.matches) {
        role.style.fontSize = "";
        return;
      }
      const target = name.getBoundingClientRect().width;
      if (target < 8) return;
      const probe = document.createElement("span");
      probe.className = "hello-role";
      probe.textContent = role.textContent ?? "";
      probe.style.cssText =
        "position:fixed;left:-9999px;top:0;visibility:hidden;max-width:none;";
      document.body.appendChild(probe);
      let lo = 8;
      let hi = 160;
      for (let i = 0; i < 18; i += 1) {
        const mid = (lo + hi) / 2;
        probe.style.fontSize = `${mid}px`;
        if (probe.getBoundingClientRect().width > target) hi = mid;
        else lo = mid;
      }
      probe.remove();
      role.style.fontSize = `${lo}px`;
    };

    const observer = new ResizeObserver(fit);
    observer.observe(name);
    mobile.addEventListener("change", fit);
    document.fonts.ready.then(fit);
    fit();
    return () => {
      observer.disconnect();
      mobile.removeEventListener("change", fit);
    };
  }, []);

  useLayoutEffect(() => {
    const row = positionRef.current;
    if (!row) return;
    const divider = 29;
    const sync = () => {
      const phrases = [
        ...row.querySelectorAll<HTMLElement>(".hello-position-phrase"),
      ];
      const rules = [
        ...row.querySelectorAll<HTMLElement>(".hello-position-rule"),
      ];
      const available = row.clientWidth;
      let used = 0;
      phrases.forEach((phrase, index) => {
        const width = phrase.getBoundingClientRect().width;
        if (index === 0) {
          used = width;
          return;
        }
        const rule = rules[index - 1];
        const fits = used + divider + width <= available + 0.5;
        rule.hidden = !fits;
        used = fits ? used + divider + width : width;
      });
    };
    const observer = new ResizeObserver(sync);
    observer.observe(row);
    document.fonts.ready.then(sync);
    sync();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const page = document.getElementById("hello");
    if (!page || reduced.matches) return;
    page.classList.add("is-open");
  }, []);

  return (
    <div className="hello-page" id="hello">
      <main className="hello-main">
        <div className="hello-intro">
          <h1 className="name-lockup">
            <img ref={nameRef} className="name-wordmark" src={nameBlockUrl} alt="" />
            <img className="name-bubble" src={nameBlockUrl} alt="" aria-hidden="true" />
            <span className="sr-only">
              Arielle Schussler. You can call me R.E.L.
            </span>
          </h1>
          <p ref={roleRef} className="hello-role">
            Product design leader &amp; principal-level product designer
          </p>
          <p ref={positionRef} className="hello-position">
            {POSITION.map((phrase, index) => (
              <Fragment key={phrase}>
                {index > 0 ? (
                  <span className="hello-position-rule" aria-hidden="true" />
                ) : null}
                <span className="hello-position-phrase">{phrase}</span>
              </Fragment>
            ))}
          </p>
        </div>
        <div className="hello-lower">
          <p className="hello-quote">
            <span className="hello-quote-text">
              You can see what <br />
              makes a design good. <br />
              You can’t always see what <br />
              makes a design <span className="hello-great">great.</span>
            </span>
          </p>
          <MiniRelCoffee />
        </div>
      </main>
    </div>
  );
}
